/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getAttributeValuesAsync, getByIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    buildCacheKey,
    generateImageUrl,
    getProductUrlSync,
    getSelectedProductIdFromActionInput
} from '@msdyn365-commerce-modules/retail-actions';

import { IPageSummaryData } from '@msdyn365-commerce-modules/page-summary';
import { IOnlyCProductPageSummaryConfig } from './only-c-product-page-summary.props.autogenerated';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';

/**
 * Product Page Summary Input
 */
export class ProductPageSummaryInput implements IActionInput {
    public apiSettings: ICommerceApiSettings;

    public config: IOnlyCProductPageSummaryConfig;

    public productId: number;

    constructor(config: IOnlyCProductPageSummaryConfig, apiSettings: ICommerceApiSettings, productId: number) {
        this.config = config || {};
        this.apiSettings = apiSettings;
        this.productId = productId;
    }

    public getCacheObjectType = (): string => 'ProductPageSummary';

    public getCacheKey = (): string => `${buildCacheKey('ProductPageSummary', this.apiSettings)}-${this.config.title}-${this.productId}`;

    public dataCacheType = (): CacheType => 'application';
}

export interface IProductPageSummary extends IPageSummaryData {
    price?: number;
    productId?: number;
    productBrand?: string;
}

const createInput = (args: ICreateActionContext) => {
    const productId = getSelectedProductIdFromActionInput(args);

    if (productId) {
        return new ProductPageSummaryInput(<IOnlyCProductPageSummaryConfig>args.config, args.requestContext.apiSettings, +productId);
    }
    throw new Error('Unable to create ProductPageSummaryAction input, no productId found on module config or query');
};

// Checks product attributes for the presence of brand attribute to extract the brand name for product metadata
const getProductBrand = (productAttributes: AttributeValue[]): string => {
    return productAttributes?.find((item: AttributeValue) => item.Name === 'Brand')?.TextValue ?? '';
};

/**
 *
 * @param productAttributes Fetch description from one of two product attributes
 * @param config
 * @returns
 */
const getProductDescription = (productAttributes: AttributeValue[], config: IOnlyCProductPageSummaryConfig): string => {
    const { customDescriptionAttr, friendlyDescriptionAttr } = config;
    let pDescription = productAttributes?.find(
        (item: AttributeValue) => item.Name === (customDescriptionAttr ?? 'FriendlyDescriptionCustom')
    )?.TextValue;

    if (!pDescription || pDescription === '') {
        pDescription = productAttributes?.find((item: AttributeValue) => item.Name === (friendlyDescriptionAttr ?? 'FriendlyDescription'))
            ?.TextValue;
    }
    return pDescription?.replace('- ', '. ') ?? '';
};

const getProductName = (productName: string): string => {
    return productName
        .split(' ')
        .map(word => {
            // product names often in all caps, whcih schema.org doesn't like
            if (word.length > 1 && word === word.toUpperCase()) {
                return word[0].toUpperCase() + word.substring(1).toLowerCase();
            }
            return word;
        })
        .join(' ');
};

const action = async (input: ProductPageSummaryInput, context: IActionContext): Promise<IProductPageSummary> => {
    const { config, productId, apiSettings } = input;
    const simpleProductPromise = getByIdAsync({ callerContext: context }, productId, apiSettings.channelId);
    const productAttributesPromise = getAttributeValuesAsync(
        { callerContext: context, queryResultSettings: {} },
        productId,
        apiSettings.channelId,
        apiSettings.catalogId
    );
    return Promise.all([simpleProductPromise, productAttributesPromise])
        .then(results => {
            const simpleProductData = results[0];
            const productAttributes = results[1];
            let productUrl: string | undefined;
            try {
                productUrl = getProductUrlSync(simpleProductData, context);
                const canonicalDomain = context.requestContext.canonicalDomain;
                if (productUrl && canonicalDomain) {
                    productUrl = `https://${canonicalDomain}${productUrl}`.toLocaleLowerCase();
                } else {
                    productUrl = undefined;
                }
            } catch {
                productUrl = undefined;
            }
            return {
                title: getProductName(simpleProductData.Name ?? ''),
                description: getProductDescription(productAttributes, config),
                sharingImageUrl: generateImageUrl(simpleProductData.PrimaryImageUrl, apiSettings),
                canonicalUrl: productUrl,
                faviconUrl: config && config.faviconUrl,
                productId: simpleProductData.RecordId,
                price: simpleProductData.AdjustedPrice,
                productBrand: getProductBrand(productAttributes)
            };
        })
        .catch(e => {
            // If the action fails fallback to values defined from config
            if (config) {
                return {
                    title: config.title,
                    description: config.description,
                    sharingImageUrl: config.sharingImage && config.sharingImage.src,
                    faviconUrl: config && config.faviconUrl
                };
            }
            return {};
        });
};

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/page-summary/product-page-summary',
    action: <IAction<IProductPageSummary>>action,
    input: createInput
});
